/** @format */

import { memo } from 'react'
import { CopyBlock, ocean } from 'react-code-blocks'

import styles from 'dir/styles/courses.module.css'
import promise_introImg from 'assets/images/promise-intro.png'
import promise_statesImg from 'assets/images/promise-states.png'
import creating_promiseImg from 'assets/images/creating-promise.png'
import chainingImg from 'assets/images/chaining.png'

export const _3 = memo(() => (
  <section className={styles.section}>
    <h2 className={styles.h2}>3 - Promise API</h2>
    <p className={styles.description}>
      <b>Promise (обещание, промис)</b> — объект, представляющий текущее состояние асинхронной операции. Удобный способ
      организации асинхронного кода.
    </p>
    <p className={styles.description}>У промиса есть 2 состояния: </p>
    <ul className={styles.list}>
      <li className={styles.list__item}>
        <b>Pending</b> — ожидание, исходное состояние при создании промиса.
      </li>
      <li className={styles.list__item}>
        <b>Settled</b> — выполнен, которое в свою очередь делится на две категории:{' '}
        <code className={styles.code}>fullfilled</code> — выполнено успешно и{' '}
        <code className={styles.code}>rejected</code> — выполнено с ошибкой.{' '}
      </li>
    </ul>
    <p className={styles.description}>
      Вначале промис находится в состоянии ожидания (<code className={styles.code}>pending</code>), после чего он может
      выполнится успешно (<code className={styles.code}>fulfilled</code>) или с ошибкой (
      <code className={styles.code}>rejected</code>). Когда промис переходит в состояние выполнен (
      <code className={styles.code}>settled</code>), с результатом или ошибкой – это навсегда. Грубо говоря, промис -
      это болванка для данных, значение которых мы не знаем в момент его создания.{' '}
    </p>
    <img className={styles.img} src={promise_introImg} alt="promise intro" />
    <p className={styles.description}>Способ использования: </p>
    <ul className={styles.list}>
      <li className={styles.list__item}>
        Код, которому надо сделать что-то асинхронно, создаёт обещание и возвращает его.
      </li>
      <li className={styles.list__item}>Внешний код, получив обещание, навешивает на него обработчики. </li>
      <li className={styles.list__item}>
        По завершении процесса асинхронный код переводит обещание в состояние{' '}
        <code className={styles.code}>fullfilled</code> или <code className={styles.code}>rejected</code>. При этом
        автоматически вызываются обработчики во внешнем коде.
      </li>
    </ul>
    <img className={styles.img} src={promise_statesImg} alt="promise states" />
    <p className={styles.description}>Отличия промиса и callback-функции: </p>
    <ul className={styles.list}>
      <li className={styles.list__item}>Коллбэки - это функции, обещания это объекты. </li>
      <li className={styles.list__item}>Внешний код, получив обещание, навешивает на него обработчики. </li>
      <li className={styles.list__item}>
        Коллбэки передаются в качестве аргументов из внешнего кода во внутренний, обещания возвращаются из внутреннего
        кода во внешний.
      </li>
      <li className={styles.list__item}>
        Коллбэки обрабатывают успешное или неуспешное завершение, обещания ничего не обрабатывают.
      </li>
      <li className={styles.list__item}>Внешний код, получив обещание, навешивает на него обработчики. </li>
      <li className={styles.list__item}>
        Коллбэки могут обрабатывать несколько событий, обещания связаны только с одним событием.
      </li>
    </ul>
    <p className={styles.description}>
      Обещания (помимо читабельности, которая является побочным положительным эффектом) используются в основном для
      композиции, цепочки вызовов и обработки результата и ошибок в отдельных логических ветках.
    </p>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>3.1 - Создание</h3>
      <p className={styles.description}>
        Обещание создается как экземпляр класса <code className={styles.code}>Promise</code> с одной функцией в качестве
        аргумента. Вызов конструктора немедленно исполнит функцию <code className={styles.code}>fn</code>, переданную в
        качестве аргумента. Цель этой функции состоит в информировании экземпляра (промиса), когда событие, с которым он
        связан, будет завершено.
      </p>
      <CopyBlock
        text={`const promise = new Promise((resolve, reject) => {
  /*
   * Эта функция будет вызвана автоматически. В ней можно выполнять
   * любые асинхронные операции. Когда они завершатся — нужно
   * вызвать одно из: resolve(результат) при успешном выполнении,
   * или reject(ошибка) при ошибке.
   */
});`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>Параметры передаваемой функции: </p>
      <ul className={styles.list}>
        <li className={styles.list__item}>
          <code className={styles.code}>resolve(arg)</code> — функция, которую необходимо вызвать при успешной операции.
          Переданный в нее аргумент будет значением выполненного промиса.
        </li>
        <li className={styles.list__item}>
          <code className={styles.code}>reject(arg)</code> — функция, которую необходимо вызвать при ошибке. Переданный
          в нее аргумент будет значением ошибки которое можно будет обработать.
        </li>
      </ul>
      <img className={styles.img} src={creating_promiseImg} alt="creating promise" />
      <CopyBlock
        text={`const promise = new Promise((resolve, reject) => {
  setTimeout(() => {
    resolve('success!');
  }, 2000);
});`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        В переменную <code className={styles.code}>promise</code> будет записан объект обещания в состоянии{' '}
        <code className={styles.code}>pending</code>, а через 2 секунды, после того как будет вызван{' '}
        <code className={styles.code}>resolve("success!")</code>, промис перейдет в состояние{' '}
        <code className={styles.code}>fullfilled</code>.
      </p>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>3.2 - Использование</h3>
      <p className={styles.description}>
        После того как промис создан, с ним можно работать используя методы <code className={styles.code}>then</code> и{' '}
        <code className={styles.code}>catch</code>, которые доступны через его прототип. Код пишется так, как будто мы
        размышляем о том, что может произойти если промис выполнится или нет, не думая о временных рамках.
      </p>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>3.2.1 - then</h4>
        <p className={styles.description}>
          Позволяет выполнить код, в котором можно получить доступ и обработать результат промиса.
        </p>
        <CopyBlock
          text={`promise.then(onResolve, onReject)`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
        <p className={styles.description}>
          В метод <code className={styles.code}>then</code> передаются две функции, которые будут вызваны когда промис
          перейдет в состояние выполнен (settled).
        </p>
        <ul className={styles.list}>
          <li className={styles.list__item}>
            <code className={styles.code}>onResolve(arg)</code> — будет вызвана при успешном выполнении промиса и
            получит результат промиса как аргумент (то, что передаем в вызов{' '}
            <code className={styles.code}>resolve</code>).
          </li>
          <li className={styles.list__item}>
            <code className={styles.code}>onReject(arg)</code> — будет вызвана при выполнении промиса с ошибкой и
            получит ошибку как аргумент (то, что передаем в вызов <code className={styles.code}>reject</code>).
          </li>
        </ul>
        <CopyBlock
          text={`const promise = new Promise((resolve, reject) => {
  setTimeout(() => {
    /*
     * Если какое-то условие выполняется, то есть все хорошо,
     * вызываем resolve и получает данные. Условие зависит от задачи.
     */
    resolve('Data passed into resolve function :)');

    // Если что-то не так, вызываем reject и передаем ошибку
    // reject("Error passed into reject function :(")
  }, 2000);
});

// Выполнится мгновенно
console.log('BEFORE promise.then');

const onResolve = data => {
  console.log('INSIDE promise.then - onResolve');
  console.log(data); // "Data passed into resolve function :)"
};

const onReject = error => {
  console.log('INSIDE promise.then - onReject');
  console.log(error); // "Error passed into reject function :("
};

promise.then(
  // будет вызвана через 2 секунды, если обещание выполнится успешно
  onResolve,
  // будет вызвана через 2 секунды, если обещание выполнится с ошибкой
  onReject,
);

// Выполнится мгновенно
console.log('AFTER promise.then');`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
        <p className={styles.description}>
          Если <code className={styles.code}>onResolve</code> и <code className={styles.code}>onReject</code> не
          содержат сложной логики, их объявляют как инлайн функции в методе <code className={styles.code}>then</code>.
        </p>
        <CopyBlock
          text={`const promise = new Promise((resolve, reject) => {
  setTimeout(() => {
    // Если все ок, то вызывается resolve и передаем данные
    resolve('Data passed into resolve function :)');

    // Если что-то не так, вызваем reject и передаем ошибку
    // reject("Error passed into reject function :(")
  }, 2000);
});

// Выполнится мгновенно
console.log('BEFORE promise.then');

promise.then(
  // Будет вызвана через 2 секунды, если обещание выполнится успешно
  data => {
    console.log('INSIDE promise.then - onResolve');
    console.log(data); // "Data passed into resolve function :)"
  },
  // Будет вызвана через 2 секунды, если обещание выполнится с ошибкой
  error => {
    console.log('INSIDE promise.then - onReject');
    console.log(error); // "Error passed into reject function :("
  },
);

// Выполнится мгновенно
console.log('AFTER promise.then');`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
      </section>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>3.2.2 - catch</h4>
        <p className={styles.description}>
          Немного дальше мы узнаем о цепочках промисов, а пока научимся обрабатывать ошибки не в колбеке{' '}
          <code className={styles.code}>onReject</code> метода <code className={styles.code}>then</code>, а в
          специальном методе <code className={styles.code}>catch</code>. Обрабатывать ошибки очень удобно, используя
          метод <code className={styles.code}>catch</code> только один раз, в конце цепочки.{' '}
        </p>
        <CopyBlock text={`promise.catch(onReject)`} language="javascript" showLineNumbers={false} theme={ocean} />
        <p className={styles.description}>
          Хендлер для обработки состояния <code className={styles.code}>reject</code>, исполнится только если промис
          исполнится с ошибкой ( <b>rejected</b>). <code className={styles.code}>onReject(arg)</code> будет вызвана при
          выполнении промиса с ошибкой, и получит ошибку как аргумент (то, что передаем в вызов{' '}
          <code className={styles.code}>reject</code>).{' '}
        </p>
        <p className={styles.description}>
          Создадим обещание, сделаем задержку на 2 секунды, вызовем <code className={styles.code}>reject</code>,
          имитируя выполнение промиса с ошибкой.{' '}
        </p>
        <CopyBlock
          text={`const promise = new Promise((resolve, reject) => {
  setTimeout(() => {
    reject('There was an error :(');
  }, 2000);
});

/*
 * then не выполнится так как в функции fn, внутри new Promise(fn),
 * был вызван reject(). А catch как раз выполнится через 2 секунды
 */
promise
  .then(data => {
    console.log(data);
  })
  .catch(error => {
    console.log(error);
  });`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
      </section>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>3.2.3 - finally</h4>
        <p className={styles.description}>
          Этот метод может быть полезен, если вы хотите выполнить некоторую обработку или очистку после того, как
          обещание будет исполнено, независимо от результата.{' '}
        </p>
        <p className={styles.description}>
          Позволяет выполнить указанную callback-функцию после того, как обещание будет разрешено (выполнено или
          отклонено). Позволяет избежать дублирования кода в обработчиках <code className={styles.code}>then()</code> и{' '}
          <code className={styles.code}>catch()</code>. Возвращает обещание.{' '}
        </p>
        <CopyBlock
          text={`promise.finally(() => {
  // settled (fulfilled или rejected)
});`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
        <p className={styles.description}>
          Функция обратного вызова не получит никаких аргументов, поскольку нельзя точно определить выполнено ли
          обещание или отклонено. Тут будет выполняться код, который зависит только от времени его исполнения, значение
          промиса не важно.{' '}
        </p>
        <CopyBlock
          text={`const promise = new Promise((resolve, reject) => {
  setTimeout(() => {
    resolve('success!');
  }, 2000);
});

promise
  .then(data => console.log(data)) // "success"
  .catch(error => console.log(error))
  .finally(() => console.log('finished!')); // "finished"`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
      </section>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>3.3 - Цепочки промисов</h3>
      <p className={styles.description}>
        <b>Чейнинг (chaining)</b> — возможность строить асинхронные цепочки из промисов. Одна из основных причин
        существования и активного использования промисов.{' '}
      </p>
      <p className={styles.description}>
        Каждый метод <code className={styles.code}>then</code>, результатом своего выполнения, возвращает промис. Его
        значением будет то, что возвращается из callback-функции <code className={styles.code}>onResolve</code>.{' '}
      </p>
      <img className={styles.img} src={chainingImg} alt="chaining" />
      <CopyBlock
        text={`asyncFn(...)
  .then(...)
  .then(...)
  .then(...)
  .catch(...);`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Так как <code className={styles.code}>then</code> возвращает промис, до его выполнения может пройти некоторое
        время, поэтому оставшаяся часть цепочки будет ждать.{' '}
      </p>
      <CopyBlock
        text={`const promise = new Promise((resolve, reject) => {
  setTimeout(() => {
    resolve(5);
  }, 2000);
});

promise
  .then(value => {
    console.log(value); // 5
    return value * 2;
  })
  .then(value => {
    console.log(value); // 10
    return value * 3;
  })
  .then(value => {
    console.log(value); // 30
  })
  .catch(error => {
    console.log(error);
  });`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        При возникновении ошибки в любом месте цепочки, выполнение всех последующих{' '}
        <code className={styles.code}>then</code> отменяется, а управление передается методу{' '}
        <code className={styles.code}>catch</code>.
      </p>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>3.4 - Статические методы класса Promise</h3>
      <p className={styles.description}>
        Иногда бывают ситуации, когда нам необходимо дождаться выполнения не одного, а сразу набора промисов и только
        тогда что-то сделать. Бывают и такие ситуации, когда из набора промисов необходимо дождаться выполнения любого
        одного, проигнорировав остальные.{' '}
      </p>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>3.4.1 - Promise.all()</h4>
        <p className={styles.description}>
          Статический метод, получает массив промисов и ждет их исполнения, возвращает промис.{' '}
        </p>
        <CopyBlock
          text={`Promise.all([promise1, promise2, ...])`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
        <p className={styles.description}>
          При успешном выполнении всех промисов из массива, промис, возвращаемый из{' '}
          <code className={styles.code}>Promise.all</code>, перейдет в состояние{' '}
          <code className={styles.code}>settled -> fullfilled</code>, а его значением будет массив результатов
          исполнения каждого промиса.{' '}
        </p>
        <p className={styles.description}>
          Если в массиве промисов хотя бы один исполнился с ошибкой, то перейдет в состояние{' '}
          <code className={styles.code}>settiled -> rejected</code>, а значением промиса будет ошибка.{' '}
        </p>
        <p className={styles.description}>
          Давайте напишем функцию, которая будет принимать текст для <code className={styles.code}>resolve</code> и
          задержку в мс, а результатом своего выполнения будет возвращать промис. Затем создадим 2 промиса с разным
          временем задержки.{' '}
        </p>
        <CopyBlock
          text={`const makePromise = (text, delay) => {
  return new Promise(resolve => {
    setTimeout(() => resolve(text), delay);
  });
};

const promiseA = makePromise('promiseA', 1000);
const promiseB = makePromise('promiseB', 3000);

/*
 * Выполнится спустя 3 секунды, когда выполнится второй промис с задержкой в 3c.
 * Первый выполнится через секунду и просто будет готов
 */
Promise.all([promiseA, promiseB])
  .then(result => console.log(result)) //["promiseA", "promiseB"]
  .catch(err => console.log(err));`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
      </section>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>3.4.2 - Promise.race()</h4>
        <CopyBlock
          text={`Promise.race([promise1, promise2, ...])`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
        <p className={styles.description}>
          Статический метод, получает массив промисов и возвращает обещание. Когда хотя бы одно обещание в массиве
          исполнилось, исполнится возвращаемый промис, а все остальные будут отброшены.{' '}
        </p>
        <CopyBlock
          text={`const makePromise = (text, delay) => {
  return new Promise(resolve => {
    setTimeout(() => resolve(text), delay);
  });
};

const promiseA = makePromise('promiseA', 1000);
const promiseB = makePromise('promiseB', 3000);

/*
 * Выполнится спустя 1 секунду, когда выполнится самый быстрый promiseA
 * с задержкой в 1c. Второй промис promiseB будет проигнорирован
 */
Promise.race([promiseA, promiseB])
  .then(result => console.log(result)) // "promiseA"
  .catch(err => console.log(err));`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
      </section>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>3.5 - Promise.resolve(), Promise.reject() и Promise.finally()</h3>
      <p className={styles.description}>
        Вызов статического метода <code className={styles.code}>Promise.resolve(value)</code> создаёт успешно
        выполнившийся промис с результатом <code className={styles.code}>value</code>. Это аналогично{' '}
        <code className={styles.code}>new Promise((resolve) => resolve(value))</code>, только короче. Этот метод
        используют, когда хотят построить асинхронную цепочку и начальный результат уже есть.{' '}
      </p>
      <p className={styles.description}>
        Это можно использовать для того, чтобы заменить callback на цепочку промисов. То есть вместо, того чтобы
        передавать callback в функцию и надеяться на лучшее, получаем промис и чейним{' '}
        <code className={styles.code}>then</code>, в котором доступен результат работы функции.{' '}
      </p>
      <CopyBlock
        text={`const getMessage = function (callback) {
  const input = prompt('Введите сообщение');

  callback(input);
};

const logger = message => console.log(message);

getMessage(logger);`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>Превращается в следующее. </p>
      <CopyBlock
        text={`const getMessage = function () {
  const input = prompt('Введите сообщение');

  return Promise.resolve(input);
};

const logger = message => console.log(message);

getMessage().then(message => logger(message));

// Или еще короче
getMessage().then(logger);`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Аналогично <code className={styles.code}>Promise.reject(error)</code> создаёт уже выполнившийся промис, но не с
        успешным результатом, а с ошибкой <code className={styles.code}>error</code>. Он используется крайне редко,
        потому что ошибка возникает обычно не в начале цепочки, а в процессе её выполнения.{' '}
      </p>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>3.6 - Дополнительные материалы</h3>
      <ul className={styles.list}>
        <li className={styles.list__item}>
          <a
            className={styles.link}
            href="https://habr.com/ru/company/nix/blog/323066/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Промисы на примере бургер-вечеринки (обязательно к прочтению)
          </a>
        </li>
        <li className={styles.list__item}>
          <a
            className={styles.link}
            href="https://exploringjs.com/es6/ch_promises.html"
            target="_blank"
            rel="noopener noreferrer"
          >
            Promises for asynchronous programming
          </a>
        </li>
      </ul>
    </section>
  </section>
))
